import {
    PublicClientApplication,
    InteractionRequiredAuthError,
    type AccountInfo,
    type AuthenticationResult
} from "@azure/msal-browser";

import { msalConfig, LOGIN_SCOPES, MODULE_SCOPES, type ModuleId } from "./config.ts";

/**
 * Token broker.
 *
 * Refresh tokens issued to SPAs are capped at 24 hours and cannot be extended
 * (ARCHITECTURE.md §6). Silent acquisition therefore fails partway through any
 * long export. The contract here is that `acquireToken` throws
 * `InteractionRequiredError` rather than prompting on its own, so the caller —
 * the job queue, from step 4 — can pause and checkpoint before the popup opens.
 * Prompting from inside the broker would lose in-flight state.
 */

export class InteractionRequiredError extends Error {
    readonly scopes: readonly string[];

    constructor(scopes: readonly string[]) {
        super("Interactive sign-in required");
        this.name = "InteractionRequiredError";
        this.scopes = scopes;
    }
}

export class NotSignedInError extends Error {
    constructor() {
        super("No signed-in account");
        this.name = "NotSignedInError";
    }
}

let msal: PublicClientApplication | null = null;

export async function initAuth(): Promise<AccountInfo | null> {
    msal = new PublicClientApplication(msalConfig);
    await msal.initialize();

    // Completes a redirect sign-in if we came back from one.
    const redirectResult = await msal.handleRedirectPromise();
    if (redirectResult?.account) {
        msal.setActiveAccount(redirectResult.account);
        return redirectResult.account;
    }

    const [existing] = msal.getAllAccounts();
    if (existing) {
        msal.setActiveAccount(existing);
        return existing;
    }

    return null;
}

function client(): PublicClientApplication {
    if (!msal) {
        throw new Error("initAuth() has not been called");
    }
    return msal;
}

export function getAccount(): AccountInfo | null {
    return msal?.getActiveAccount() ?? null;
}

export async function signIn(): Promise<AccountInfo> {
    const result = await client().loginPopup({ scopes: [...LOGIN_SCOPES] });
    client().setActiveAccount(result.account);
    return result.account;
}

export async function signOut(): Promise<void> {
    const account = getAccount();
    await client().logoutPopup(account ? { account } : {});
}

/**
 * Silent-first token acquisition. Throws `InteractionRequiredError` instead of
 * prompting — see the note at the top of this file.
 */
export async function acquireToken(scopes: readonly string[]): Promise<string> {
    const account = getAccount();
    if (!account) {
        throw new NotSignedInError();
    }

    try {
        const result: AuthenticationResult = await client().acquireTokenSilent({
            scopes: [...scopes],
            account
        });
        return result.accessToken;
    } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
            throw new InteractionRequiredError(scopes);
        }
        throw err;
    }
}

/**
 * Interactive consent. Call this only from a user gesture, after the caller has
 * paused whatever it was doing and persisted its checkpoint.
 */
export async function acquireTokenInteractive(scopes: readonly string[]): Promise<string> {
    const account = getAccount();
    const result = await client().acquireTokenPopup({
        scopes: [...scopes],
        ...(account ? { account } : {})
    });
    client().setActiveAccount(result.account);
    return result.accessToken;
}

/** Incremental consent for one module's scope group (ARCHITECTURE.md §4). */
export async function consentToModule(module: ModuleId): Promise<void> {
    await acquireTokenInteractive(MODULE_SCOPES[module]);
}
